import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate, fmtDT } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';

// ── P&L Statement ─────────────────────────────────────────────────────────────
export function ProfitLossPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0,10),
    toDate:   new Date().toISOString().slice(0,10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/pnl?fromDate=${filters.fromDate}&toDate=${filters.toDate}`).then(r=>r.data);
      if (r.success) setData(r.data);
    } catch(e) { toast.error('Failed to load P&L'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const Row = ({ label, value, bold, indent, color }) => (
    <div className="flex-between" style={{ padding:'7px 0', borderBottom:'1px solid var(--gray-100)', paddingLeft: indent ? 24 : 0 }}>
      <span style={{ fontSize:'.875rem', fontWeight: bold?600:400, color: color||'var(--gray-700)' }}>{label}</span>
      <span style={{ fontSize:'.875rem', fontWeight: bold?600:400, color: color||(value<0?'var(--danger)':'var(--gray-800)') }}>{fmtPKR(Math.abs(value||0))}{value<0?' (Loss)':''}</span>
    </div>
  );

  return (
    <AppShell title="Profit & Loss Statement" actions={
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <input className="form-control" type="date" style={{ width:140 }} value={filters.fromDate} onChange={e=>setFilters(f=>({...f,fromDate:e.target.value}))} />
        <span style={{ fontSize:'.8rem', color:'var(--gray-400)' }}>to</span>
        <input className="form-control" type="date" style={{ width:140 }} value={filters.toDate}   onChange={e=>setFilters(f=>({...f,toDate:e.target.value}))} />
        <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>{loading?'Loading…':'Apply'}</button>
        <button className="btn btn-outline btn-sm" onClick={()=>window.print()}>🖨️ Print</button>
      </div>
    }>
      {loading ? <Spinner /> : data ? (
        <div style={{ maxWidth:680 }}>
          {/* Summary KPIs */}
          <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:24 }}>
            <div className="stat-card">
              <div className="stat-label">Total Revenue</div>
              <div className="stat-value" style={{ color:'var(--success)', fontSize:'1.1rem' }}>{fmtPKR(data.income.totalRevenue)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Net Profit</div>
              <div className="stat-value" style={{ color:data.netProfit>=0?'var(--success)':'var(--danger)', fontSize:'1.1rem' }}>
                {fmtPKR(Math.abs(data.netProfit))}
                <span style={{ fontSize:'.75rem', marginLeft:6 }}>{data.netProfit<0?'Loss':''}</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Net Margin</div>
              <div className="stat-value" style={{ color:data.netMarginPct>=0?'var(--success)':'var(--danger)', fontSize:'1.1rem' }}>{data.netMarginPct}%</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">ECL — Income Statement</span>
              <span style={{ fontSize:'.78rem', color:'var(--gray-400)' }}>{fmtDate(data.period.from)} – {fmtDate(data.period.to)}</span>
            </div>
            <div className="card-body">
              <div style={{ fontWeight:600, fontSize:'.85rem', textTransform:'uppercase', letterSpacing:'.06em', color:'var(--gray-500)', marginBottom:4 }}>Income</div>
              <Row label="Domestic courier revenue"    value={data.income.domesticRevenue}      indent />
              <Row label="International revenue"       value={data.income.internationalRevenue} indent />
              <Row label="COD commission"               value={data.income.codCommission}        indent />
              <Row label="Gross Revenue"               value={data.income.totalRevenue}         bold />

              <div style={{ fontWeight:600, fontSize:'.85rem', textTransform:'uppercase', letterSpacing:'.06em', color:'var(--gray-500)', margin:'16px 0 4px' }}>Expenses</div>
              <Row label="Operating expenses"          value={data.expenses.operatingExpense}   indent />
              <Row label="Refunds & credit notes"      value={data.expenses.refundsCost}        indent />
              <Row label="Payroll costs"               value={data.expenses.payrollCost}        indent />
              <Row label="Total Expenses"              value={data.expenses.totalExpenses}      bold color="var(--danger)" />

              <div style={{ borderTop:'2px solid var(--navy)', marginTop:12, paddingTop:12 }}>
                <Row label="Gross Profit"              value={data.grossProfit}                 bold color={data.grossProfit>=0?'var(--success)':'var(--danger)'} />
                <Row label="Tax provision (29%)"       value={-data.taxAmount}                  indent color="var(--gray-600)" />
                <Row label="Net Profit / (Loss)"       value={data.netProfit}                   bold color={data.netProfit>=0?'var(--success)':'var(--danger)'} />
              </div>

              <div style={{ marginTop:16, padding:12, background:'var(--gray-50)', borderRadius:8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <div style={{ fontSize:'.72rem', color:'var(--gray-400)', textTransform:'uppercase' }}>Gross Margin</div>
                  <div style={{ fontWeight:700, fontSize:'1.2rem', color:data.grossMarginPct>=0?'var(--success)':'var(--danger)' }}>{data.grossMarginPct}%</div>
                </div>
                <div>
                  <div style={{ fontSize:'.72rem', color:'var(--gray-400)', textTransform:'uppercase' }}>Net Margin</div>
                  <div style={{ fontWeight:700, fontSize:'1.2rem', color:data.netMarginPct>=0?'var(--success)':'var(--danger)' }}>{data.netMarginPct}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : <div style={{ textAlign:'center', padding:60, color:'var(--gray-400)' }}>Select a date range and click Apply</div>}
    </AppShell>
  );
}

// ── Bank Reconciliation ───────────────────────────────────────────────────────
export function BankReconciliationPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().slice(0,10),
    toDate:   new Date().toISOString().slice(0,10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/bank-recon?fromDate=${filters.fromDate}&toDate=${filters.toDate}`).then(r=>r.data);
      if (r.success) setData(r.data);
    } finally { setLoading(false); }
  }, [filters]);

  return (
    <AppShell title="Bank Reconciliation" actions={
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <input className="form-control" type="date" style={{ width:140 }} value={filters.fromDate} onChange={e=>setFilters(f=>({...f,fromDate:e.target.value}))} />
        <input className="form-control" type="date" style={{ width:140 }} value={filters.toDate}   onChange={e=>setFilters(f=>({...f,toDate:e.target.value}))} />
        <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>{loading?'…':'Run'}</button>
      </div>
    }>
      {loading ? <Spinner /> : data ? (
        <div style={{ display:'grid', gap:16 }}>
          <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)' }}>
            <div className="stat-card"><div className="stat-label">Invoice Collections</div><div className="stat-value" style={{ color:'var(--success)', fontSize:'1.1rem' }}>{fmtPKR(data.summary.totalInvoicePaid)}</div></div>
            <div className="stat-card"><div className="stat-label">Payments Recorded</div><div className="stat-value" style={{ color:'var(--info)', fontSize:'1.1rem' }}>{fmtPKR(data.summary.totalPaymentRecorded)}</div></div>
            <div className="stat-card"><div className="stat-label">COD Deposited</div><div className="stat-value" style={{ color:'var(--amber)', fontSize:'1.1rem' }}>{fmtPKR(data.summary.totalCODDeposited)}</div></div>
          </div>
          {[
            { title:'Invoice Collections', rows:data.invoiceDeposits, cols:['Date','Amount','Transactions'] },
            { title:'COD Deposits', rows:data.codDeposits, cols:['Date','Amount','Batches'] },
          ].map(section=>(
            <div key={section.title} className="card">
              <div className="card-header"><span className="card-title">{section.title}</span></div>
              <div className="table-wrap">
                {section.rows.length ? (
                  <table><thead><tr>{section.cols.map(c=><th key={c}>{c}</th>)}</tr></thead>
                    <tbody>{section.rows.map((r,i)=>(
                      <tr key={i}>
                        <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{fmtDate(r.date)}</td>
                        <td style={{ fontWeight:600 }}>{fmtPKR(r.amount)}</td>
                        <td>{r.txn_count||r.batch_count||0}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <EmptyState icon="🏦" title={`No ${section.title.toLowerCase()} in period`} />}
              </div>
            </div>
          ))}
        </div>
      ) : <div style={{ textAlign:'center', padding:60, color:'var(--gray-400)' }}>Select date range and click Run</div>}
    </AppShell>
  );
}

// ── Expense Claims ────────────────────────────────────────────────────────────
export function ExpenseClaimsPage() {
  const { user } = useAuth();
  const [claims, setClaims]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category:'TRAVEL', amount:'', description:'', expenseDate:new Date().toISOString().slice(0,10) });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const isHR = ['HR','ADMIN','FINANCE'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/hr/expenses').then(r=>r.data);
      if (r.success) setClaims(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(()=>{ load(); },[load]);

  const submit = async () => {
    if (!form.amount||!form.category) { toast.error('Category and amount required'); return; }
    try {
      const r = await api.post('/hr/expenses', form).then(r=>r.data);
      if (r.success) { toast.success('Expense submitted'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const process = async (claimId, status) => {
    try {
      await api.patch(`/hr/expenses/${claimId}`, { status });
      toast.success(`Expense ${status.toLowerCase()}`); load();
    } catch { toast.error('Failed'); }
  };

  const SB = { PENDING:'badge-warning', APPROVED:'badge-success', REJECTED:'badge-danger', PAID:'badge-info' };
  const CATEGORIES = ['TRAVEL','FUEL','ACCOMMODATION','MEALS','EQUIPMENT','MOBILE','OTHER'];

  return (
    <AppShell title="Expense Claims" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> Submit Expense</button>
    }>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : claims.length ? (
            <table>
              <thead><tr>{isHR&&<th>Employee</th>}<th>Category</th><th>Amount</th><th>Date</th><th>Description</th><th>Status</th>{isHR&&<th>Actions</th>}</tr></thead>
              <tbody>
                {claims.map(c=>(
                  <tr key={c.claim_id}>
                    {isHR && <td style={{ fontWeight:500 }}>{c.emp_name}<div className="text-sm text-muted">{c.designation}</div></td>}
                    <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{c.category}</span></td>
                    <td style={{ fontWeight:600 }}>{fmtPKR(c.amount)}</td>
                    <td className="text-sm">{fmtDate(c.expense_date)}</td>
                    <td style={{ maxWidth:200 }}><span className="truncate text-sm" style={{ display:'block' }}>{c.description||'—'}</span></td>
                    <td><span className={`badge ${SB[c.status]||'badge-gray'}`}>{c.status}</span></td>
                    {isHR && c.status==='PENDING' && (
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>process(c.claim_id,'APPROVED')}>✓ Approve</button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>process(c.claim_id,'REJECTED')}>✗ Reject</button>
                        </div>
                      </td>
                    )}
                    {isHR && c.status!=='PENDING' && <td className="text-sm text-muted">{c.status}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="💸" title="No expense claims" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>Submit First Claim</button>} />}
        </div>
      </div>

      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Submit Expense Claim">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="exp-cat">Category <span className="req">*</span></label>
              <select id="exp-cat" className="form-control" value={form.category} onChange={set('category')}>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="exp-amt">Amount (PKR) <span className="req">*</span></label>
              <input id="exp-amt" className="form-control" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="exp-date">Expense Date <span className="req">*</span></label>
            <input id="exp-date" className="form-control" type="date" value={form.expenseDate} onChange={set('expenseDate')} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="exp-desc">Description</label>
            <textarea id="exp-desc" className="form-control" rows={3} placeholder="Details of expense..." value={form.description} onChange={set('description')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Submit Claim</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Performance Appraisals ────────────────────────────────────────────────────
export function AppraisalsPage() {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [form, setForm] = useState({
    empId:'', period:'', overallRating:'', comments:'', nextGoals:'',
    kpiScores:{ delivery:3, attendance:3, customer_service:3, teamwork:3 }
  });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/hr/appraisals').then(r=>r.data);
      if (r.success) setAppraisals(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(()=>{ load(); },[load]);

  const submit = async () => {
    if (!form.empId||!form.overallRating) { toast.error('Employee and rating required'); return; }
    try {
      const r = await api.post('/hr/appraisals', form).then(r=>r.data);
      if (r.success) { toast.success('Appraisal recorded'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const stars = (n) => '★'.repeat(Math.round(n||0)) + '☆'.repeat(5-Math.round(n||0));

  return (
    <AppShell title="Performance Appraisals" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> New Appraisal</button>
    }>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : appraisals.length ? (
            <table>
              <thead><tr><th>Employee</th><th>Period</th><th>Rating</th><th>Stars</th><th>Appraiser</th><th>Date</th></tr></thead>
              <tbody>
                {appraisals.map(a=>(
                  <tr key={a.appraisal_id}>
                    <td><div style={{ fontWeight:500 }}>{a.emp_name}</div><div className="text-sm text-muted">{a.designation}</div></td>
                    <td className="text-sm">{a.period||'—'}</td>
                    <td style={{ fontWeight:700, fontSize:'1.1rem', color:a.overall_rating>=4?'var(--success)':a.overall_rating>=3?'var(--amber)':'var(--danger)' }}>{a.overall_rating}/5</td>
                    <td style={{ color:'var(--amber)', letterSpacing:2 }}>{stars(a.overall_rating)}</td>
                    <td className="text-sm">{a.appraiser_name||'—'}</td>
                    <td className="text-sm">{fmtDate(a.appraisal_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="⭐" title="No appraisals yet" />}
        </div>
      </div>

      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="New Performance Appraisal">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Employee ID <span className="req">*</span></label>
              <input className="form-control" placeholder="Employee UUID" value={form.empId} onChange={set('empId')} />
            </div>
            <div className="form-group">
              <label className="form-label">Period</label>
              <input className="form-control" placeholder="e.g. Q1 2026" value={form.period} onChange={set('period')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Overall Rating (1–5) <span className="req">*</span></label>
            <input className="form-control" type="number" min="1" max="5" step="0.5" value={form.overallRating} onChange={set('overallRating')} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            {Object.entries(form.kpiScores).map(([kpi, score])=>(
              <div key={kpi} className="form-group" style={{ margin:0 }}>
                <label className="form-label" style={{ fontSize:'.78rem' }}>{kpi.replace(/_/g,' ').toUpperCase()} (1–5)</label>
                <input className="form-control" type="number" min="1" max="5" step="0.5" value={score}
                  onChange={e=>setForm(f=>({...f,kpiScores:{...f.kpiScores,[kpi]:parseFloat(e.target.value)}}))} />
              </div>
            ))}
          </div>
          <div className="form-group">
            <label className="form-label">Comments</label>
            <textarea className="form-control" rows={3} value={form.comments} onChange={set('comments')} />
          </div>
          <div className="form-group">
            <label className="form-label">Goals for Next Period</label>
            <textarea className="form-control" rows={2} value={form.nextGoals} onChange={set('nextGoals')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Save Appraisal</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Tele-Sales / Lead Management ──────────────────────────────────────────────
export function LeadsPage() {
  const [leads, setLeads]       = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('leads');
  const [showAdd, setShowAdd]   = useState(false);
  const [showCall, setShowCall] = useState(null); // selected lead
  const [filters, setFilters]   = useState({ status:'' });
  const [form, setForm]   = useState({ companyName:'', contactName:'', contactPhone:'', contactEmail:'', source:'COLD_CALL', notes:'' });
  const [callForm, setCallForm] = useState({ duration:'', outcome:'SPOKE', notes:'', nextAction:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const setC = k => e => setCallForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, cl] = await Promise.all([
        api.get(`/leads${filters.status?`?status=${filters.status}`:''}`).then(r=>r.data),
        api.get('/leads/call-logs').then(r=>r.data),
      ]);
      if (l.success)  setLeads(l.data);
      if (cl.success) setCallLogs(cl.data);
    } finally { setLoading(false); }
  }, [filters.status]);
  useEffect(()=>{ load(); },[load]);

  const createLead = async () => {
    if (!form.companyName) { toast.error('Company name required'); return; }
    try {
      const r = await api.post('/leads', form).then(r=>r.data);
      if (r.success) { toast.success('Lead created'); setShowAdd(false); load(); }
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const logCall = async () => {
    if (!showCall) return;
    try {
      await api.post('/leads/call-logs', { leadId:showCall.lead_id, ...callForm, duration:parseInt(callForm.duration||0) });
      toast.success('Call logged'); setShowCall(null); load();
    } catch { toast.error('Failed'); }
  };

  const updateStatus = async (leadId, status) => {
    await api.patch(`/leads/${leadId}`, { status }).catch(()=>{});
    load();
  };

  const STATUS_COLORS = { NEW:'badge-info', CONTACTED:'badge-warning', QUALIFIED:'badge-navy', PROPOSAL:'badge-purple', WON:'badge-success', LOST:'badge-danger', ON_HOLD:'badge-gray' };
  const SOURCES = ['COLD_CALL','WALK_IN','REFERRAL','WEBSITE','SOCIAL_MEDIA','EMAIL','OTHER'];
  const OUTCOMES = ['NO_ANSWER','LEFT_MESSAGE','SPOKE','CONVERTED','NOT_INTERESTED','CALLBACK_REQUESTED'];

  return (
    <AppShell title="Tele-Sales & Lead Management" actions={
      <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}><Plus size={14}/> New Lead</button>
    }>
      <div className="tabs">
        <div className={`tab${tab==='leads'?' active':''}`} onClick={()=>setTab('leads')}>Leads ({leads.length})</div>
        <div className={`tab${tab==='calls'?' active':''}`} onClick={()=>setTab('calls')}>Call Log ({callLogs.length})</div>
      </div>

      {tab==='leads' && (
        <>
          <div className="filters-bar" style={{ marginBottom:12 }}>
            <select className="form-control" style={{ width:160 }} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
              <option value="">All Statuses</option>
              {['NEW','CONTACTED','QUALIFIED','PROPOSAL','WON','LOST','ON_HOLD'].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
          </div>
          <div className="card">
            <div className="table-wrap">
              {loading ? <Spinner /> : leads.length ? (
                <table>
                  <thead><tr><th>Company</th><th>Contact</th><th>Source</th><th>Status</th><th>Follow-up</th><th>Assigned</th><th>Actions</th></tr></thead>
                  <tbody>
                    {leads.map(l=>(
                      <tr key={l.lead_id}>
                        <td style={{ fontWeight:600 }}>{l.company_name}</td>
                        <td>
                          <div className="text-sm">{l.contact_name||'—'}</div>
                          <div className="text-sm text-muted">{l.contact_phone||l.contact_email||''}</div>
                        </td>
                        <td><span className="badge badge-gray" style={{ fontSize:'.65rem' }}>{l.source}</span></td>
                        <td><span className={`badge ${STATUS_COLORS[l.status]||'badge-gray'}`} style={{ fontSize:'.68rem' }}>{l.status}</span></td>
                        <td className="text-sm" style={{ color:l.next_follow_up&&new Date(l.next_follow_up)<new Date()?'var(--danger)':undefined }}>{l.next_follow_up?fmtDate(l.next_follow_up):'—'}</td>
                        <td className="text-sm">{l.assigned_name||'—'}</td>
                        <td>
                          <div style={{ display:'flex', gap:4 }}>
                            <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>setShowCall(l)}>📞 Log Call</button>
                            {l.status==='NEW' && <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateStatus(l.lead_id,'CONTACTED')}>Contact</button>}
                            {l.status==='CONTACTED' && <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateStatus(l.lead_id,'QUALIFIED')}>Qualify</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyState icon="📋" title="No leads yet" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>Add First Lead</button>}/>}
            </div>
          </div>
        </>
      )}

      {tab==='calls' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : callLogs.length ? (
              <table>
                <thead><tr><th>Company</th><th>Called By</th><th>Outcome</th><th>Duration</th><th>Notes</th><th>Date</th></tr></thead>
                <tbody>
                  {callLogs.map(c=>(
                    <tr key={c.call_id}>
                      <td style={{ fontWeight:500 }}>{c.company_name}</td>
                      <td className="text-sm">{c.caller_name}</td>
                      <td><span className={`badge ${c.outcome==='CONVERTED'?'badge-success':c.outcome==='NOT_INTERESTED'?'badge-danger':'badge-info'}`} style={{ fontSize:'.65rem' }}>{c.outcome.replace(/_/g,' ')}</span></td>
                      <td className="text-sm">{c.duration_seconds ? `${Math.floor(c.duration_seconds/60)}m ${c.duration_seconds%60}s` : '—'}</td>
                      <td style={{ maxWidth:200 }}><span className="truncate text-sm" style={{ display:'block' }}>{c.notes||'—'}</span></td>
                      <td className="text-sm">{fmtDT(c.called_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="📞" title="No call logs yet" />}
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="New Lead">
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Company Name <span className="req">*</span></label><input className="form-control" value={form.companyName} onChange={set('companyName')}/></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Contact Name</label><input className="form-control" value={form.contactName} onChange={set('contactName')}/></div>
            <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.contactPhone} onChange={set('contactPhone')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.contactEmail} onChange={set('contactEmail')}/></div>
            <div className="form-group"><label className="form-label">Source</label>
              <select className="form-control" value={form.source} onChange={set('source')}>
                {SOURCES.map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={form.notes} onChange={set('notes')}/></div>
        </div>
        <div className="modal-footer"><button className="btn btn-outline" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={createLead}>Create Lead</button></div>
      </Modal>

      {/* Log Call Modal */}
      <Modal open={!!showCall} onClose={()=>setShowCall(null)} title={`Log Call — ${showCall?.company_name}`}>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Outcome <span className="req">*</span></label>
              <select className="form-control" value={callForm.outcome} onChange={setC('outcome')}>
                {OUTCOMES.map(o=><option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Duration (seconds)</label><input className="form-control" type="number" min="0" value={callForm.duration} onChange={setC('duration')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Call Notes</label><textarea className="form-control" rows={3} value={callForm.notes} onChange={setC('notes')}/></div>
          <div className="form-group"><label className="form-label">Next Action</label><input className="form-control" placeholder="e.g. Send proposal by Friday" value={callForm.nextAction} onChange={setC('nextAction')}/></div>
        </div>
        <div className="modal-footer"><button className="btn btn-outline" onClick={()=>setShowCall(null)}>Cancel</button><button className="btn btn-primary" onClick={logCall}>Save Call Log</button></div>
      </Modal>
    </AppShell>
  );
}
